"use client";

import * as React from "react";

/**
 * Performance utilities for the design system.
 * These utilities help optimize component rendering and bundle size.
 */

// =============================================================================
// LAZY LOADING
// =============================================================================

/**
 * Creates a lazily-loaded component with a loading fallback.
 * Use for heavy components like DataTable, DateRangePicker, etc.
 *
 * @example
 * const LazyDataTable = lazyWithFallback(
 *   () => import("@/registry/new-york/blocks/data-table/data-table"),
 *   <Skeleton variant="table-row" />
 * );
 */
export function lazyWithFallback<P extends object>(
  importFn: () => Promise<{ default: React.ComponentType<P> }>,
  fallback: React.ReactNode = null
): React.FC<P> {
  const LazyComponent = React.lazy(importFn);

  return function LazyWrapper(props: P) {
    return (
      <React.Suspense fallback={fallback}>
        <LazyComponent {...props} />
      </React.Suspense>
    );
  };
}

/**
 * Preload a lazy component before it's needed.
 * Call this on hover/focus to reduce perceived loading time.
 *
 * @example
 * const importDataTable = () => import("./DataTable");
 * <Button onMouseEnter={() => preloadComponent(importDataTable)}>
 *   Open DataTable
 * </Button>
 */
export function preloadComponent<T>(
  importFn: () => Promise<{ default: T }>
): void {
  importFn();
}

// =============================================================================
// MEMOIZATION
// =============================================================================

/**
 * A stable callback that doesn't change between renders.
 * Unlike useCallback, this always returns the same function reference.
 *
 * @example
 * const handleClick = useStableCallback((id: string) => {
 *   doSomething(id);
 * });
 */
export function useStableCallback<T extends (...args: unknown[]) => unknown>(
  callback: T
): T {
  const callbackRef = React.useRef(callback);

  React.useLayoutEffect(() => {
    callbackRef.current = callback;
  });

  return React.useCallback(
    ((...args) => callbackRef.current?.(...args)) as T,
    []
  );
}

/**
 * Memoize a value with a custom comparison function.
 * Useful when React.useMemo's shallow comparison isn't enough.
 *
 * @example
 * const memoizedData = useMemoCompare(
 *   data,
 *   (prev, next) => prev?.id === next?.id
 * );
 */
export function useMemoCompare<T>(
  value: T,
  compare: (prev: T | undefined, next: T) => boolean
): T {
  const previousRef = React.useRef<T | undefined>(undefined);
  const previous = previousRef.current;

  const isEqual = previous !== undefined && compare(previous, value);

  React.useEffect(() => {
    if (!isEqual) {
      previousRef.current = value;
    }
  });

  return isEqual ? (previous as T) : value;
}

/**
 * Debounce a value. Useful for search inputs.
 *
 * @example
 * const [search, setSearch] = useState("");
 * const debouncedSearch = useDebouncedValue(search, 300);
 */
export function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = React.useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Throttle a callback function.
 *
 * @example
 * const handleScroll = useThrottledCallback((e) => {
 *   console.log(e.scrollTop);
 * }, 100);
 */
export function useThrottledCallback<T extends (...args: unknown[]) => unknown>(
  callback: T,
  delay: number
): T {
  const lastRan = React.useRef(Date.now());
  const timeoutRef = React.useRef<NodeJS.Timeout | undefined>(undefined);

  return React.useCallback(
    ((...args) => {
      const now = Date.now();

      if (now - lastRan.current >= delay) {
        callback(...args);
        lastRan.current = now;
      } else {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          callback(...args);
          lastRan.current = Date.now();
        }, delay - (now - lastRan.current));
      }
    }) as T,
    [callback, delay]
  );
}

// =============================================================================
// INTERSECTION OBSERVER
// =============================================================================

/**
 * Detect when an element enters the viewport.
 * Useful for lazy loading content or triggering animations.
 *
 * @example
 * const { ref, isIntersecting } = useIntersectionObserver({
 *   threshold: 0.5,
 *   triggerOnce: true,
 * });
 *
 * return <div ref={ref}>{isIntersecting && <HeavyComponent />}</div>;
 */
export function useIntersectionObserver(options?: {
  threshold?: number | number[];
  root?: Element | null;
  rootMargin?: string;
  triggerOnce?: boolean;
}) {
  const [isIntersecting, setIsIntersecting] = React.useState(false);
  const [hasTriggered, setHasTriggered] = React.useState(false);
  const ref = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (options?.triggerOnce && hasTriggered) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting;
        setIsIntersecting(isVisible);

        if (isVisible && options?.triggerOnce) {
          setHasTriggered(true);
          observer.disconnect();
        }
      },
      {
        threshold: options?.threshold ?? 0,
        root: options?.root ?? null,
        rootMargin: options?.rootMargin ?? "0px",
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [options?.threshold, options?.root, options?.rootMargin, options?.triggerOnce, hasTriggered]);

  return { ref, isIntersecting };
}

// =============================================================================
// VIRTUAL LIST
// =============================================================================

interface VirtualListOptions {
  /** Total number of items */
  itemCount: number;
  /** Height of each item in pixels */
  itemHeight: number;
  /** Number of items to render above/below the visible area */
  overscan?: number;
}

interface VirtualListResult {
  /** Ref to attach to the scroll container */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Total height of all items (for scroll sizing) */
  totalHeight: number;
  /** Items to render with their index and style */
  virtualItems: Array<{
    index: number;
    start: number;
    style: React.CSSProperties;
  }>;
}

/**
 * Virtualize a long list for performance.
 * Only renders items that are visible in the viewport.
 *
 * @example
 * const { containerRef, totalHeight, virtualItems } = useVirtualList({
 *   itemCount: 10000,
 *   itemHeight: 40,
 *   overscan: 5,
 * });
 *
 * return (
 *   <div ref={containerRef} style={{ height: 400, overflow: "auto" }}>
 *     <div style={{ height: totalHeight, position: "relative" }}>
 *       {virtualItems.map(({ index, style }) => (
 *         <div key={index} style={style}>
 *           Row {index}
 *         </div>
 *       ))}
 *     </div>
 *   </div>
 * );
 */
export function useVirtualList(options: VirtualListOptions): VirtualListResult {
  const { itemCount, itemHeight, overscan = 3 } = options;
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = React.useState(0);
  const [containerHeight, setContainerHeight] = React.useState(0);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    setContainerHeight(container.clientHeight);

    const handleScroll = () => {
      setScrollTop(container.scrollTop);
    };

    const handleResize = () => {
      setContainerHeight(container.clientHeight);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const totalHeight = itemCount * itemHeight;

  // Calculate visible range
  const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
  const endIndex = Math.min(
    itemCount - 1,
    Math.ceil((scrollTop + containerHeight) / itemHeight) + overscan
  );

  const virtualItems: VirtualListResult["virtualItems"] = [];

  for (let i = startIndex; i <= endIndex; i++) {
    const start = i * itemHeight;
    virtualItems.push({
      index: i,
      start,
      style: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: itemHeight,
        transform: `translateY(${start}px)`,
      },
    });
  }

  return {
    containerRef,
    totalHeight,
    virtualItems,
  };
}

// =============================================================================
// PERFORMANCE MONITORING
// =============================================================================

/**
 * Measure render time of a component.
 * Only active in development mode.
 *
 * @example
 * useRenderTime("DataTable");
 */
export function useRenderTime(componentName: string) {
  React.useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;

    const startTime = performance.now();

    return () => {
      const endTime = performance.now();
      console.debug(`[Perf] ${componentName} rendered in ${(endTime - startTime).toFixed(2)}ms`);
    };
  });
}

/**
 * Track re-renders and their causes.
 * Only active in development mode.
 *
 * @example
 * useWhyDidYouRender("MyComponent", { propA, propB, state });
 */
export function useWhyDidYouRender(
  componentName: string,
  props: Record<string, unknown>
) {
  const previousProps = React.useRef<Record<string, unknown> | undefined>(undefined);

  React.useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;

    if (previousProps.current) {
      const changedProps: Record<string, { from: unknown; to: unknown }> = {};

      Object.keys({ ...previousProps.current, ...props }).forEach((key) => {
        if (previousProps.current![key] !== props[key]) {
          changedProps[key] = {
            from: previousProps.current![key],
            to: props[key],
          };
        }
      });

      if (Object.keys(changedProps).length > 0) {
        console.debug(`[Why Render] ${componentName}:`, changedProps);
      }
    }

    previousProps.current = props;
  });
}
